import { Link } from "react-router-dom";
import PortfolioCarousel from "./PortfolioCarousel";
import { useT } from "../../i18n/LanguageContext";
import { track } from "../../lib/analytics";

// Static, non-translatable data (images, urls, slugs, taxonomy) — joined by index with i18n copy.
// Taxonomy keys map to labels in dictionaries[lang].portfolio.filters.*.
export const workAssets = [
    {
        slug: "terry-barber",
        kind: "screenshot",
        industry: "music-arts",
        productTypes: ["public-site", "chatbot", "analytics"],
        outcomes: ["self-serve", "capture-leads", "understand-users"],
        href: "https://terrybarber.com/",
        images: [
            "/portfolio/terrybarber/01.jpg",
            "/portfolio/terrybarber/02.jpg",
            "/portfolio/terrybarber/03.jpg",
        ],
    },
    {
        slug: "dge-cigars-erp",
        kind: "screenshot",
        industry: "manufacturing",
        productTypes: ["internal-erp", "automation"],
        outcomes: ["automate-ops", "save-time", "understand-users"],
        images: [
            "/portfolio/dgecigars/01-orders.png",
            "/portfolio/dgecigars/02-detail.png",
            "/portfolio/dgecigars/03-login.png",
        ],
    },
    {
        slug: "scalater-command-center",
        kind: "screenshot",
        industry: "saas-ops",
        productTypes: ["internal-dashboard", "ai"],
        outcomes: ["automate-ops", "understand-users", "save-time"],
        images: [
            "/portfolio/scalater/01-analytics.png",
            "/portfolio/scalater/02-requests.png",
            "/portfolio/scalater/03-processes.png",
        ],
    },
    {
        slug: "lopez-hill",
        kind: "screenshot",
        industry: "hospitality",
        productTypes: ["public-site", "chatbot", "internal-dashboard"],
        outcomes: ["capture-leads", "sell-more", "self-serve"],
        href: "https://lopezhill.appscidentally.com/",
        images: [
            "/portfolio/lopezhill/01-hero.png",
            "/portfolio/lopezhill/02-tequila.png",
            "/portfolio/lopezhill/03-admin.png",
        ],
    },
    {
        slug: "island-flavors",
        kind: "screenshot",
        industry: "food-beverage",
        productTypes: ["public-site", "internal-dashboard"],
        outcomes: ["self-serve", "sell-more", "save-time"],
        href: "https://islandflavor.appscidentally.com/",
        images: [
            "/portfolio/islandflavors/01-hero.png",
            "/portfolio/islandflavors/02-menu.png",
            "/portfolio/islandflavors/03-admin.png",
        ],
    },
];

export default function Portfolio() {
    const { t } = useT();
    const copies = t("portfolio.works");
    const works = workAssets.map((a, i) => ({ ...a, ...copies[i] }));

    return (
        <section
            id="work"
            data-testid="portfolio-section"
            className="bg-surface/60 py-24 md:py-32 lg:py-40 border-y border-warm"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-24">
                    <div className="lg:col-span-8 reveal">
                        <p className="overline mb-6">{t("portfolio.overline")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-ink">
                            {t("portfolio.headlineA")}{" "}
                            <em className="not-italic text-terracotta">
                                {t("portfolio.headlineEm")}
                            </em>{" "}
                            {t("portfolio.headlineB")}
                        </h2>
                    </div>
                </div>

                <div className="space-y-20 md:space-y-24 lg:space-y-28">
                    {works.map((w, i) => (
                        <article
                            key={w.slug}
                            data-testid={`portfolio-case-${i}`}
                            className="reveal grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start"
                        >
                            <div
                                className={`lg:col-span-7 ${
                                    i % 2 === 1 ? "lg:order-2" : ""
                                }`}
                            >
                                <PortfolioCarousel
                                    images={w.images}
                                    alt={w.client}
                                    href={w.href}
                                    kind={w.kind}
                                    testid={`portfolio-case-${i}-carousel`}
                                />
                            </div>
                            <div
                                className={`lg:col-span-5 ${
                                    i % 2 === 1 ? "lg:order-1 lg:pr-6" : "lg:pl-6"
                                }`}
                            >
                                <span className="font-mono text-terracotta text-sm tracking-[0.16em]">
                                    {w.n} · {w.client}
                                </span>
                                {w.outcomeKicker && (
                                    <div
                                        className="mt-3 font-mono uppercase text-terracotta text-xs sm:text-sm tracking-[0.18em] leading-snug border-l-2 border-terracotta pl-3"
                                        data-testid={`portfolio-case-${i}-outcome-kicker`}
                                    >
                                        {w.outcomeKicker}
                                    </div>
                                )}
                                <div className="overline mt-3">
                                    {w.category} · {w.location}
                                </div>
                                <h3 className="mt-4 font-serif text-2xl md:text-3xl lg:text-[2.1rem] tracking-tight leading-tight text-ink">
                                    {w.title}
                                </h3>

                                <p className="mt-5 text-[0.98rem] leading-relaxed text-secondary">
                                    {w.intro}
                                </p>

                                <div className="mt-6 flex flex-wrap gap-2">
                                    {w.meta.map((m) => (
                                        <span
                                            key={m}
                                            className="text-[0.68rem] font-mono tracking-wide uppercase border border-warm text-ink/80 px-3 py-1"
                                        >
                                            {m}
                                        </span>
                                    ))}
                                </div>

                                <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
                                    <Link
                                        to={`/case-studies/${w.slug}`}
                                        data-testid={`portfolio-case-${i}-case-study-link`}
                                        onClick={() =>
                                            track("case_study_opened", {
                                                slug: w.slug,
                                                industry: w.industry,
                                                source: "home_portfolio",
                                            })
                                        }
                                        className="inline-flex items-center gap-2 bg-ink text-sand px-5 py-3 text-sm tracking-wide hover:bg-[var(--ink-soft)] transition-colors"
                                    >
                                        {t("portfolio.readCaseStudy")}
                                        <span aria-hidden>→</span>
                                    </Link>
                                    {w.href ? (
                                        <a
                                            href={w.href}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`portfolio-case-${i}-link`}
                                            onClick={() =>
                                                track("live_site_visited", {
                                                    slug: w.slug,
                                                    industry: w.industry,
                                                    source: "home_portfolio",
                                                    url: w.href,
                                                })
                                            }
                                            className="text-sm link-underline text-ink"
                                        >
                                            {t("portfolio.visit")} {w.client.toLowerCase()} →
                                        </a>
                                    ) : (
                                        <span
                                            data-testid={`portfolio-case-${i}-private`}
                                            className="text-xs tracking-[0.2em] uppercase text-secondary font-mono"
                                        >
                                            {t("portfolio.privateNote")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                <p className="mt-20 reveal text-center font-serif text-2xl md:text-3xl text-secondary max-w-3xl mx-auto leading-snug">
                    {t("portfolio.bottomNote")}
                </p>

                <div className="mt-10 reveal flex justify-center">
                    <Link
                        to="/case-studies"
                        data-testid="portfolio-see-all-link"
                        className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-3.5 text-sm tracking-wide hover:bg-ink hover:text-sand transition-colors"
                    >
                        {t("portfolio.seeAll")}
                        <span aria-hidden>→</span>
                    </Link>
                </div>
            </div>
        </section>
    );
}
